import { CloseOutlined } from "@ant-design/icons";
import { Button, Card, Checkbox, Col, DatePicker, Form, Input, Modal, Row, Select, Typography, Upload } from "antd";
import { Member } from "interfaces/users";
import React, { useEffect, useState } from "react";
/* import ImgCrop from "antd-img-crop"; */
import axios from "axios";
import { message } from "components/Messages/Messages";
import moment from "moment";
import { uploadButton } from "components/UploadButton/UploadButton";
import { getBase64 } from "helpers/functions";

const { Title } = Typography;

interface Props {
  member: Member;
  onClose: () => void;
  onEdit: (member: Member) => void;
  onDeleted: (id: number) => void;
}

const EditMember: React.FC<Props> = ({ member, onClose, onEdit, onDeleted }) => {
  const [form] = Form.useForm();

  const [file, setFile] = useState<File | null>(null);

  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);

  const [loading, setLoading] = useState<boolean>(false);

  const [removeImage, setRemoveImage] = useState<boolean>(false);

  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    form.setFieldsValue({
      ...member,
      birthday: member.birthday ? moment(member.birthday) : null,
    });

    setFile(null);
    setPreview(null);
    setRemoveImage(false);
  }, [form, member]);

  const updateMember = (values: Member) => {
    const { birthday, ...otherValues } = values;

    const formData = new FormData();
    Object.entries(otherValues).forEach(([key, value]) => {
      formData.append(key, String(value));
    });

    if (birthday) {
      const formattedBirthday = moment(birthday).format("YYYY-MM-DD");
      formData.append("birthday", formattedBirthday);
    }

    if (file) {
      formData.append("profile_picture", file);
    }

    if (removeImage) {
      formData.append("profile_picture", "");
    }

    setLoading(true);
    axios
      .put<Member>(`/internal/members/${member.id}/`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then(response => {
        onClose();
        message("success", "Medlem oppdatert");
        onEdit(response.data);
        setLoading(false);
      })
      .catch(() => {
        message("error", "Kunne ikke oppdatere medlemm");
        setLoading(false);
      });
  };

  const deleteMember = () => {
    Modal.confirm({
      title: `Er du sikker på at du vil slette ${member.full_name} som medlem? Medlemmet vil bli deaktivert og skjult, men studenten vil fortsatt eksistere i systemet. Dersom studenten senere skal legges til som medlem igjen kan medlemsobjektet reaktiveres i databasen.`,
      okText: "Slett",
      okType: "danger",
      cancelText: "Avbryt",
      onOk: () => {
        axios
          .delete<Member>(`/internal/members/${member.id}/`)
          .then(() => {
            onClose();
            onDeleted(member.id);
            message("success", "Medlem slettet");
          })
          .catch(() => {
            message("error", "Kunne ikke slette medlemm");
          });
      },
    });
  };

  useEffect(() => {
    function generateOptions() {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;

      const temporaryOptions: React.SetStateAction<{ value: string; label: string }[]> = [];

      for (let i = 0; i < 5; i += 1) {
        if (currentMonth >= 8) {
          // If august or later, open for being a member of the current year
          const optionValue = `Høst ${(currentYear - i) % 100}`;
          temporaryOptions.push({ value: optionValue, label: optionValue });
        } else {
          /*     const year = currentYear - i - 1; */
          const optionValue = `Høst ${(currentYear - i - 1) % 100}`;
          temporaryOptions.push({ value: optionValue, label: optionValue });
        }
      }

      if (!temporaryOptions.some(option => option.value === member.seniority)) {
        temporaryOptions.push({ value: member.seniority, label: member.seniority });
      }

      setOptions(temporaryOptions);
    }
    generateOptions();
  }, [member.seniority]);

  useEffect(
    function setPreviewImage() {
      if (file) {
        getBase64(file)
          .then((result: string | null) => {
            setPreview(result);
          })
          .catch(() => {
            setPreview(null);
          });
      }
    },
    [file]
  );

  return (
    <>
      <Row id="edit-member" style={{ paddingTop: "16px" }} key={member.id}>
        <Col span={24}>
          <Card>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => onClose()}
              style={{ position: "absolute", top: 20, right: 15 }}
            />
            <Title level={4}>{member.full_name}</Title>
            <Form form={form} layout="vertical" requiredMark onFinish={updateMember}>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Navn" name="full_name">
                    <Input placeholder="Skriv inn navn..." />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Bindeleddetmail" name="bl_email">
                    <Input placeholder="Skriv inn BL-mail..." />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Mobilnummer" name="mobile">
                    <Input placeholder="Skriv inn BL-mail..." />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Bursdag" name="birthday">
                    <DatePicker style={{ width: "100%" }} placeholder="dd.mm.åååå" format="DD.MM.yyyy" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Medlem siden" name="seniority">
                    <Select options={options} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Verv" name="duties">
                    <Input placeholder="Skriv inn lederverv..." />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={12}>
                  <Form.Item label="Profilbilde">
                    <Upload
                      listType="picture-card"
                      className="avatar-uploader"
                      maxCount={2}
                      beforeUpload={f => {
                        setFile(f);
                        setRemoveImage(false);

                        // Return false to prevent immediate upload
                        return false;
                      }}
                      onRemove={() => {
                        setFile(null);
                        setPreview(null);
                        setRemoveImage(true);
                      }}
                      fileList={
                        removeImage
                          ? []
                          : file
                          ? preview
                            ? [
                                {
                                  uid: "-1",
                                  name: "New profile picture",
                                  url: preview,
                                },
                              ]
                            : []
                          : member.profile_picture
                          ? [
                              {
                                uid: "-1",
                                name: member.profile_picture?.substring(member.profile_picture?.lastIndexOf("/") + 1),
                                status: "done",
                                url: member.profile_picture,
                              },
                            ]
                          : []
                      }
                    >
                      {uploadButton}
                    </Upload>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Aktivt medlem" name="is_active_member" valuePropName="checked">
                    <Checkbox style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Vis på nettsiden" name="show_on_bl_page" valuePropName="checked">
                    <Checkbox />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={5}>
                <Col span={8}>
                  <Button type="primary" danger onClick={deleteMember} style={{ width: "100%" }}>
                    Slett
                  </Button>
                </Col>
                <Col span={16}>
                  <Button type="primary" htmlType="submit" style={{ width: "100%" }} loading={loading}>
                    Lagre
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default EditMember;
